import React from 'react';
import { Result, Spin } from 'antd';
import usePropelAuth from '../../hooks/usePropelAuth';

interface Props {
  /** Any one of these grants access. */
  anyOf: string[];
  children: React.ReactNode;
}

/**
 * Route-level role gate.
 *
 * The app's only route protection is AuthGuard, which checks `isLoggedIn` and nothing
 * else, so every screen is reachable by any authenticated PropelAuth user. That is
 * defensible for the reference-data screens; it is not defensible for a screen with a
 * button that publishes numbers to calyxglobal.com.
 *
 * This is a usability control, not the security boundary. The server re-checks the same
 * roles on every route and additionally asserts the caller is Calyx Global staff, so
 * hiding the screen here only saves someone from a 403 they would get anyway.
 */
const RequireRole: React.FC<Props> = ({ anyOf, children }) => {
  const auth = usePropelAuth();

  if (!auth?.isLoggedIn) {
    return (
      <div className="flex justify-center py-16">
        <Spin />
      </div>
    );
  }

  const roles = (auth.roles ?? []).filter(Boolean);
  if (!roles.some((r) => anyOf.includes(r))) {
    return (
      <Result
        status="403"
        title="You do not have access to this screen"
        subTitle={
          `It needs one of: ${anyOf.join(', ')}. ` +
          'Ask a super admin to grant the role in PropelAuth.'
        }
      />
    );
  }

  return <>{children}</>;
};

export default RequireRole;
